/**
 * Entity view service
 *
 * Loads the SHACL view configuration and resolves the values of property
 * shapes for one entity (or a set of nested nodes).
 *
 * Values are read with one SELECT against the authenticated SPARQL endpoint:
 * every property path becomes a UNION branch, so a page costs one round trip
 * however many properties its shapes declare. When that query fails (for
 * instance for anonymous visitors, who only have the public /describe
 * endpoint), paths made of forward predicates are followed with DESCRIBE
 * calls instead and the other paths are reported as unavailable.
 */

import sparqlService, { describeEntity } from '@services/sparql.service';
import {
  forwardPredicates,
  pathToSparql,
  type ShapePath,
  type ViewPropertyShape,
  type ViewShapes,
} from '@utils/viewShapes.utils';
import type { EntityObject, EntityProperty, SparqlBinding } from '@/types';

/** The entity page shapes of dim-semantics, mounted next to the search config. */
export const VIEW_SHAPES_URL = '/semantics-config/shacl-view.ttl';

/** Values fetched per property; one more than this means "there are more". */
export const VALUE_LIMIT = 200;

/** Upper bound of intermediate nodes followed per step in DESCRIBE mode. */
const DESCRIBE_FAN_OUT = 20;

let shapesPromise: Promise<ViewShapes> | null = null;

/** Fetch and parse the view shapes once per page load; a failure is retried next time. */
export function loadViewShapes(): Promise<ViewShapes> {
  if (!shapesPromise) {
    shapesPromise = fetch(VIEW_SHAPES_URL, { headers: { Accept: 'text/turtle' } })
      .then((response) => {
        // Not deployed yet (no dim/semantics image mounted): 404, or the SPA's
        // index.html fallback. No shapes means every entity keeps the technical view.
        const isHtml = response.headers.get('Content-Type')?.includes('text/html') ?? false;
        if (response.status === 404 || (response.ok && isHtml)) return '';
        if (!response.ok) {
          throw new Error(`View shapes not available: ${response.status} ${response.statusText}`);
        }
        return response.text();
      })
      // The parser pulls in n3; load it only when an entity page needs the shapes.
      .then(async (turtle) => (await import('@utils/viewShapes.parser')).parseViewShapes(turtle))
      .catch((error: unknown) => {
        shapesPromise = null;
        throw error;
      });
  }
  return shapesPromise;
}

/** Values per property shape id, per subject. */
export type SubjectValues = Record<string, EntityObject[]>;

export interface ResolvedValues {
  /** subject IRI → property shape id → values */
  values: Record<string, SubjectValues>;
  /** Property shape ids whose values could not be read in the current mode. */
  unavailable: Set<string>;
}

function toEntityObject(binding: SparqlBinding[string]): EntityObject {
  return {
    value: binding.value,
    type: binding.type === 'uri' ? 'uri' : binding.type === 'bnode' ? 'bnode' : 'literal',
    datatype: binding.datatype,
    lang: binding['xml:lang'],
  };
}

function addValue(
  target: Record<string, SubjectValues>,
  subject: string,
  id: string,
  value: EntityObject
): void {
  const bySubject = (target[subject] ??= {});
  const list = (bySubject[id] ??= []);
  if (!list.some((existing) => existing.value === value.value && existing.lang === value.lang)) {
    list.push(value);
  }
}

/**
 * One SELECT for all properties of all subjects. Each branch is a subquery
 * with its own LIMIT so one large inverse relation cannot crowd out the rest.
 */
export function buildValuesQuery(subjects: string[], properties: ViewPropertyShape[]): string {
  // Property shapes that share a path are told apart by the class of their values,
  // the way Sparnatural reads them (e.g. ^bno:inOperationalPoint for tracks, signals, ...).
  const pathCounts = new Map<string, number>();
  for (const property of properties) {
    const path = pathToSparql(property.path);
    pathCounts.set(path, (pathCounts.get(path) ?? 0) + 1);
  }

  const branches = subjects.flatMap((subject) =>
    properties.map((property, index) => {
      const path = pathToSparql(property.path);
      const classFilter =
        property.classIri && (pathCounts.get(path) ?? 0) > 1
          ? ` ?value a <${property.classIri}> .`
          : '';
      return `  { SELECT ?s ?p ?value WHERE { BIND(<${subject}> AS ?s) BIND(${index} AS ?p) ?s ${path} ?value .${classFilter} } LIMIT ${VALUE_LIMIT + 1} }`;
    })
  );
  return `SELECT ?s ?p ?value WHERE {\n${branches.join('\n  UNION\n')}\n}`;
}

async function resolveWithSparql(
  subjects: string[],
  properties: ViewPropertyShape[]
): Promise<ResolvedValues> {
  const bindings = await sparqlService.query(buildValuesQuery(subjects, properties));
  const values: Record<string, SubjectValues> = {};
  for (const row of bindings) {
    const subject = row.s?.value;
    const property = properties[Number(row.p?.value)];
    if (!subject || !property || !row.value) continue;
    addValue(values, subject, property.id, toEntityObject(row.value));
  }
  return { values, unavailable: new Set() };
}

function objectsOf(properties: EntityProperty[], predicate: string): EntityObject[] {
  return properties.find((property) => property.predicate === predicate)?.objects ?? [];
}

async function resolveWithDescribe(
  subjects: string[],
  properties: ViewPropertyShape[],
  known: Record<string, EntityProperty[]>
): Promise<ResolvedValues> {
  const described = new Map<string, Promise<EntityProperty[]>>();
  const describe = (uri: string): Promise<EntityProperty[]> => {
    if (known[uri]) return Promise.resolve(known[uri]);
    if (!described.has(uri))
      described.set(
        uri,
        describeEntity(uri).catch(() => [])
      );
    return described.get(uri)!;
  };

  const values: Record<string, SubjectValues> = {};
  const unavailable = new Set<string>();

  await Promise.all(
    subjects.flatMap((subject) =>
      properties.map(async (property) => {
        const predicates = forwardPredicates(property.path);
        // Without SPARQL the value classes cannot be checked, so shared paths are left out too.
        const sharedPath = properties.some(
          (other) => other !== property && pathToSparql(other.path) === pathToSparql(property.path)
        );
        if (!predicates || sharedPath) {
          unavailable.add(property.id);
          return;
        }
        let frontier: EntityObject[] = [{ value: subject, type: 'uri' }];
        for (const predicate of predicates) {
          const nodes = frontier.filter((node) => node.type === 'uri').slice(0, DESCRIBE_FAN_OUT);
          const next = await Promise.all(nodes.map((node) => describe(node.value)));
          frontier = next.flatMap((nodeProperties) => objectsOf(nodeProperties, predicate));
        }
        for (const value of frontier.slice(0, VALUE_LIMIT + 1))
          addValue(values, subject, property.id, value);
      })
    )
  );

  return { values, unavailable };
}

/** One row of a table row: a linked resource and its column values by dview:key. */
export interface TableItem {
  iri: string;
  cells: Record<string, EntityObject[]>;
}

export interface TableValues {
  items: TableItem[];
  /** More linked resources exist than were read. */
  truncated: boolean;
}

/**
 * Only the items whose km (any of the item paths) lies within the subject's own km (the lowest and
 * highest of the subject paths), widened by pad metres: the objects inside a tunnel, not along its
 * whole tracks.
 */
export interface KmWindow {
  subject: ShapePath[];
  item: ShapePath[];
  pad: number;
}

const anyOf = (paths: ShapePath[]) => paths.map((path) => `(${pathToSparql(path)})`).join('|');

/**
 * The linked resources of a row with their columns (the property shapes of its sh:node, by
 * dview:key), in one query: the resources first, each column an OPTIONAL branch of a UNION.
 */
export function buildTableQuery(
  subject: string,
  property: ViewPropertyShape,
  columns: ViewPropertyShape[],
  limit: number,
  window?: KmWindow
): string {
  const classFilter = property.classIri ? ` ?item a <${property.classIri}> .` : '';
  const windowFilter =
    window && window.subject.length > 0 && window.item.length > 0
      ? ` ?item ${anyOf(window.item)} ?km . { SELECT (MIN(?own) AS ?lo) (MAX(?own) AS ?hi) WHERE { <${subject}> ${anyOf(window.subject)} ?own } } FILTER(?km >= ?lo - ${window.pad} && ?km <= ?hi + ${window.pad})`
      : '';
  const branches = columns
    .map((column, index) => `{ BIND(${index} AS ?c) ?item ${pathToSparql(column.path)} ?value . }`)
    .join(' UNION ');
  const optional = columns.length > 0 ? `\n  OPTIONAL { ${branches} }` : '';
  return `SELECT ?item ?c ?value WHERE {
  { SELECT DISTINCT ?item WHERE { <${subject}> ${pathToSparql(property.path)} ?item .${classFilter}${windowFilter} } LIMIT ${limit + 1} }${optional}
}`;
}

export async function resolveTable(
  subject: string,
  property: ViewPropertyShape,
  columns: ViewPropertyShape[],
  limit: number,
  window?: KmWindow
): Promise<TableValues> {
  const bindings = await sparqlService.query(buildTableQuery(subject, property, columns, limit, window));
  const byIri = new Map<string, TableItem>();
  for (const row of bindings) {
    const iri = row.item?.value;
    if (!iri) continue;
    let item = byIri.get(iri);
    if (!item) {
      item = { iri, cells: {} };
      byIri.set(iri, item);
    }
    const column = row.c ? columns[Number(row.c.value)] : undefined;
    if (!column || !row.value) continue;
    const key = column.key ?? column.id;
    const list = (item.cells[key] ??= []);
    if (!list.some((value) => value.value === row.value.value)) list.push(toEntityObject(row.value));
  }
  const items = Array.from(byIri.values());
  return { items: items.slice(0, limit), truncated: items.length > limit };
}

export interface IncomingGroup {
  predicate: string;
  classIri: string;
  count: number;
}

/** Superclass types kg-gen adds to every instance; the graph has no rdfs:subClassOf to tell. */
const SUPERCLASSES = [
  'http://ontology.railml.org/railtopomodel#NetEntity',
  'http://ontology.railml.org/railml3#FunctionalInfrastructureEntity',
]
  .map((iri) => `<${iri}>`)
  .join(', ');

/** What points at a resource, counted per predicate and class of the subject. */
export async function countIncoming(uri: string): Promise<IncomingGroup[]> {
  const bindings = await sparqlService.query(`SELECT ?p ?class (COUNT(DISTINCT ?s) AS ?n) WHERE {
  ?s ?p <${uri}> ; a ?class .
  FILTER(?class NOT IN (${SUPERCLASSES}) || NOT EXISTS { ?s a ?other . FILTER(?other NOT IN (${SUPERCLASSES})) })
} GROUP BY ?p ?class ORDER BY DESC(?n)`);
  return bindings
    .filter((row) => row.p && row.class && row.n)
    .map((row) => ({ predicate: row.p.value, classIri: row.class.value, count: Number(row.n.value) }));
}

export async function listIncoming(
  uri: string,
  group: Pick<IncomingGroup, 'predicate' | 'classIri'>,
  offset: number,
  pageSize: number
): Promise<string[]> {
  const bindings = await sparqlService.query(`SELECT DISTINCT ?s WHERE {
  ?s <${group.predicate}> <${uri}> ; a <${group.classIri}> .
} ORDER BY ?s LIMIT ${pageSize} OFFSET ${offset}`);
  return bindings.map((row) => row.s?.value).filter((value): value is string => Boolean(value));
}

/**
 * Resolve the values of `properties` for each subject.
 *
 * @param known - DESCRIBE results already fetched, keyed by subject, reused in fallback mode
 */
export async function resolvePropertyValues(
  subjects: string[],
  properties: ViewPropertyShape[],
  known: Record<string, EntityProperty[]> = {}
): Promise<ResolvedValues> {
  if (subjects.length === 0 || properties.length === 0) {
    return { values: {}, unavailable: new Set() };
  }
  try {
    return await resolveWithSparql(subjects, properties);
  } catch (error) {
    console.warn('[entityView] SPARQL endpoint not available, falling back to /describe:', error);
    return resolveWithDescribe(subjects, properties, known);
  }
}

export interface PeerContext {
  /** Entities of the class on the same parent, this one included. */
  count: number;
  /** km range (metres) they cover. */
  from: number | null;
  to: number | null;
}

/** How many entities of `classIri` share `parent` through `predicate`, and the km they span. */
export async function peerContext(classIri: string, predicate: string, parent: string): Promise<PeerContext> {
  const bindings = await sparqlService.query(`SELECT (COUNT(DISTINCT ?s) AS ?n) (MIN(?a) AS ?from) (MAX(?b) AS ?to) WHERE {
  ?s a <${classIri}> ; <${predicate}> <${parent}> .
  OPTIONAL { ?s <https://data.banenor.no/ontology/hasKmFrom> ?f ; <https://data.banenor.no/ontology/hasKmTo> ?t .
    BIND(IF(?f < ?t, ?f, ?t) AS ?a) BIND(IF(?f < ?t, ?t, ?f) AS ?b) }
}`);
  const row = bindings[0];
  const number = (value: string | undefined) => (value === undefined ? null : Number(value));
  return { count: Number(row?.n?.value ?? 0), from: number(row?.from?.value), to: number(row?.to?.value) };
}

export interface LineStop {
  iri: string;
  label: string;
  km: number;
}

/** Operational points on a line with their km (metres), for naming the stations around a stretch. */
export async function lineStops(line: string): Promise<LineStop[]> {
  const bindings = await sparqlService.query(`SELECT ?op (SAMPLE(?name) AS ?label) (MIN(?at) AS ?km) WHERE {
  ?op a <http://ontology.railml.org/railml3#OperationalPoint> ;
      <https://data.banenor.no/ontology/onRailwayLine> <${line}> ;
      <http://www.w3.org/2000/01/rdf-schema#label> ?name .
  { ?op <https://data.banenor.no/ontology/atKm> ?at } UNION { ?op <https://data.banenor.no/ontology/hasKmFrom> ?at }
} GROUP BY ?op ORDER BY ?km`);
  return bindings
    .filter((row) => row.op && row.km)
    .map((row) => ({ iri: row.op.value, label: row.label?.value ?? row.op.value, km: Number(row.km.value) }));
}

export interface LinkedItem {
  iri: string;
  classIri: string;
  label: string | null;
}

/**
 * What points at `subject` by `predicate` and is a `kind` (e.g. railml3:FunctionalInfrastructureEntity),
 * by its own classes, with labels: the generic equipment tiles.
 */
export async function linkedOfKind(subject: string, predicate: string, kind: string, limit = 1000): Promise<LinkedItem[]> {
  const bindings = await sparqlService.query(`SELECT ?s ?class (SAMPLE(?name) AS ?label) WHERE {
  ?s <${predicate}> <${subject}> ; a <${kind}> ; a ?class .
  FILTER(?class NOT IN (${SUPERCLASSES}))
  OPTIONAL { ?s <http://www.w3.org/2000/01/rdf-schema#label> ?name }
} GROUP BY ?s ?class LIMIT ${limit}`);
  return bindings.map((row) => ({ iri: row.s.value, classIri: row.class.value, label: row.label?.value ?? null }));
}

